import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { StickyNote, Plus, Pencil, Trash2, Pin, PinOff, Search, Bell, CheckCircle2, Circle, ArrowLeft, MessageCircle, FileDown, Send } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const COLORS = {
  yellow: { bg: 'bg-yellow-100', border: 'border-yellow-300', dot: 'bg-yellow-400' },
  green: { bg: 'bg-green-100', border: 'border-green-300', dot: 'bg-green-400' },
  blue: { bg: 'bg-blue-100', border: 'border-blue-300', dot: 'bg-blue-400' },
  pink: { bg: 'bg-pink-100', border: 'border-pink-300', dot: 'bg-pink-400' },
  orange: { bg: 'bg-orange-100', border: 'border-orange-300', dot: 'bg-orange-400' },
  purple: { bg: 'bg-purple-100', border: 'border-purple-300', dot: 'bg-purple-400' },
};

const emptyForm = { title: '', content: '', color: 'yellow', pinned: false, reminder_date: '', whatsapp_nudge: false, send_to_phone: '' };

const todayStr = () => new Date().toISOString().slice(0, 10);

const reminderStatus = (note) => {
  if (!note.reminder_date || note.is_done) return null;
  const today = todayStr();
  if (note.reminder_date < today) return 'overdue';
  if (note.reminder_date === today) return 'today';
  return 'upcoming';
};

export default function MyNotes() {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const fetchNotes = useCallback(async (q = '') => {
    try {
      const res = await axios.get(`${API}/api/notes`, { params: q ? { search: q } : {} });
      setNotes(res.data);
    } catch {
      toast.error('Failed to load notes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchNotes(); }, [fetchNotes]);

  useEffect(() => {
    const t = setTimeout(() => fetchNotes(search), 300);
    return () => clearTimeout(t);
  }, [search, fetchNotes]);

  const resetForm = () => { setForm(emptyForm); setEditing(null); setShowForm(false); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() && !form.content.trim()) { toast.error('Write something first'); return; }
    const payload = { ...form, reminder_date: form.reminder_date || null };
    try {
      if (editing) {
        if (!payload.reminder_date) { payload.clear_reminder = true; delete payload.reminder_date; }
        await axios.put(`${API}/api/notes/${editing}`, payload);
        toast.success('Note updated');
      } else {
        await axios.post(`${API}/api/notes`, payload);
        toast.success('Note added');
      }
      resetForm();
      fetchNotes(search);
    } catch {
      toast.error('Failed to save note');
    }
  };

  const handleEdit = (n) => {
    setForm({ title: n.title || '', content: n.content || '', color: n.color || 'yellow', pinned: !!n.pinned, reminder_date: n.reminder_date || '', whatsapp_nudge: !!n.whatsapp_nudge, send_to_phone: n.send_to_phone || '' });
    setEditing(n.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this note?')) return;
    try {
      await axios.delete(`${API}/api/notes/${id}`);
      toast.success('Note deleted');
      fetchNotes(search);
    } catch { toast.error('Failed to delete'); }
  };

  const quickUpdate = async (id, data) => {
    try {
      await axios.put(`${API}/api/notes/${id}`, data);
      fetchNotes(search);
    } catch { toast.error('Failed to update'); }
  };

  const [sendingId, setSendingId] = useState(null);
  const sendWhatsApp = async (n) => {
    setSendingId(n.id);
    try {
      const res = await axios.post(`${API}/api/notes/${n.id}/send-whatsapp`);
      toast.success(`WhatsApp sent to ${res.data.sent_to}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'WhatsApp send failed');
    } finally {
      setSendingId(null);
    }
  };

  const dueCount = notes.filter(n => ['today', 'overdue'].includes(reminderStatus(n))).length;

  return (
    <div className="min-h-screen bg-gray-50 pt-44 pb-12 px-4">
      <div className="container mx-auto max-w-6xl">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <Link to="/admin/tools">
              <Button variant="outline" size="sm"><ArrowLeft size={16} className="mr-1" /> Back</Button>
            </Link>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2" data-testid="my-notes-title">
              <StickyNote className="text-yellow-500" /> My Notes
            </h1>
            {dueCount > 0 && (
              <span data-testid="notes-due-badge" className="bg-red-100 text-red-700 text-xs font-semibold px-2.5 py-1 rounded-full flex items-center gap-1">
                <Bell size={12} /> {dueCount} due
              </span>
            )}
          </div>
          <Button onClick={() => { resetForm(); setShowForm(!showForm); }} data-testid="add-note-btn" className="bg-yellow-500 hover:bg-yellow-600 text-gray-900">
            <Plus size={16} className="mr-1" /> New Note
          </Button>
        </div>

        <div className="relative mb-6 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input data-testid="notes-search-input" className="pl-9" placeholder="Search notes..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>

        {showForm && (
          <div className={`mb-8 rounded-xl border-2 p-6 shadow-sm ${COLORS[form.color].bg} ${COLORS[form.color].border}`}>
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input data-testid="note-title-input" className="bg-white/70 font-semibold" placeholder="Title (optional)" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
              <textarea
                data-testid="note-content-input"
                className="w-full min-h-[100px] rounded-md border border-input bg-white/70 px-3 py-2 text-sm"
                placeholder="Write your note here..."
                value={form.content}
                onChange={e => setForm({ ...form, content: e.target.value })}
              />
              <div className="flex flex-wrap items-end gap-6">
                <div>
                  <Label className="text-xs">Colour</Label>
                  <div className="flex gap-2 mt-1">
                    {Object.keys(COLORS).map(c => (
                      <button key={c} type="button" data-testid={`note-color-${c}`} onClick={() => setForm({ ...form, color: c })}
                        className={`w-6 h-6 rounded-full ${COLORS[c].dot} ${form.color === c ? 'ring-2 ring-offset-1 ring-gray-700' : ''}`} />
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Reminder date (optional)</Label>
                  <Input data-testid="note-reminder-date-input" type="date" className="bg-white/70 mt-1" value={form.reminder_date} onChange={e => setForm({ ...form, reminder_date: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Send-to WhatsApp number (optional)</Label>
                  <Input data-testid="note-phone-input" className="bg-white/70 mt-1" placeholder="e.g. 91234567" value={form.send_to_phone} onChange={e => setForm({ ...form, send_to_phone: e.target.value })} />
                </div>
                <label className={`flex items-center gap-2 text-sm ${form.reminder_date ? 'text-gray-800' : 'text-gray-400'}`}>
                  <input type="checkbox" data-testid="note-whatsapp-toggle" disabled={!form.reminder_date} checked={form.whatsapp_nudge} onChange={e => setForm({ ...form, whatsapp_nudge: e.target.checked })} />
                  <MessageCircle size={15} className="text-green-600" /> WhatsApp nudge on that day
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-800">
                  <input type="checkbox" checked={form.pinned} onChange={e => setForm({ ...form, pinned: e.target.checked })} />
                  <Pin size={14} /> Pin to top
                </label>
              </div>
              <div className="flex gap-2">
                <Button type="submit" data-testid="note-save-btn">{editing ? 'Update Note' : 'Add Note'}</Button>
                <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
              </div>
            </form>
          </div>
        )}

        {loading ? (
          <p className="text-center text-gray-500 py-12">Loading...</p>
        ) : notes.length === 0 ? (
          <div className="text-center py-16 text-gray-500" data-testid="notes-empty-state">
            <StickyNote size={48} className="mx-auto mb-4 text-gray-300" />
            <p className="text-lg font-medium">{search ? 'No notes match your search' : 'No notes yet'}</p>
            {!search && <p className="text-sm">Click "New Note" to jot down your first note</p>}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {notes.map(n => {
              const c = COLORS[n.color] || COLORS.yellow;
              const status = reminderStatus(n);
              return (
                <div key={n.id} data-testid={`note-card-${n.id}`}
                  className={`rounded-xl border-2 p-4 shadow-sm relative flex flex-col ${c.bg} ${c.border} ${n.is_done ? 'opacity-60' : ''}`}>
                  {n.pinned && <Pin size={14} className="absolute top-3 right-3 text-gray-600 rotate-45" />}
                  {(n.title || '').trim() && (
                    <h3 className={`font-semibold text-gray-900 mb-1 pr-6 ${n.is_done ? 'line-through' : ''}`}>{n.title}</h3>
                  )}
                  {(n.content || '').trim() && (
                    <p className={`text-sm text-gray-700 whitespace-pre-wrap flex-1 ${n.is_done ? 'line-through' : ''}`}>{n.content}</p>
                  )}
                  {n.reminder_date && (
                    <div className={`mt-3 inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-full w-fit
                      ${status === 'overdue' ? 'bg-red-200 text-red-800' : status === 'today' ? 'bg-amber-200 text-amber-900' : 'bg-white/70 text-gray-600'}`}>
                      <Bell size={11} />
                      {status === 'overdue' ? `Overdue — ${n.reminder_date}` : status === 'today' ? 'Due today' : n.reminder_date}
                      {n.whatsapp_nudge && <MessageCircle size={11} className="text-green-700" />}
                    </div>
                  )}
                  <div className="flex items-center justify-between mt-3 pt-2 border-t border-black/10">
                    <button data-testid={`note-done-toggle-${n.id}`} onClick={() => quickUpdate(n.id, { is_done: !n.is_done })}
                      className="flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900">
                      {n.is_done ? <CheckCircle2 size={16} className="text-green-600" /> : <Circle size={16} />}
                      {n.is_done ? 'Done' : 'Mark done'}
                    </button>
                    <div className="flex gap-0.5">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-gray-700" data-testid={`note-pdf-${n.id}`} title="Download PDF"
                        onClick={() => window.open(`${API}/api/notes/${n.id}/pdf`, '_blank')}>
                        <FileDown size={14} />
                      </Button>
                      {(n.send_to_phone || '').trim() && (
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-green-600 hover:text-green-800" data-testid={`note-whatsapp-send-${n.id}`}
                          title={`Send WhatsApp to ${n.send_to_phone}`} disabled={sendingId === n.id} onClick={() => sendWhatsApp(n)}>
                          <Send size={14} />
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => quickUpdate(n.id, { pinned: !n.pinned })} title={n.pinned ? 'Unpin' : 'Pin'}>
                        {n.pinned ? <PinOff size={14} /> : <Pin size={14} />}
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" data-testid={`note-edit-${n.id}`} onClick={() => handleEdit(n)}>
                        <Pencil size={14} />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:text-red-700" data-testid={`note-delete-${n.id}`} onClick={() => handleDelete(n.id)}>
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
